import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import type { DBNotification } from "../types";
import { HISTORY_KEY, INBOX_KEY } from "../queryKeys";
import {
  circleMembersPrefix,
  circlesSchedulePrefix,
} from "@/features/circles/queryKeys";

/**
 * Accept / decline a circle (formerly "buddy") invite delivered as a
 * `BUDDY_INVITE` notification.
 *
 * Implemented as a single mutation with an `accept` flag (rather than
 * two siblings) so both branches share the same optimistic removal and
 * toast plumbing. Backend dispatch happens inside the
 * `respond_circle_invite` Postgres RPC which:
 *
 *   • validates the sender/recipient relationship,
 *   • inserts both sides of the circle membership on accept,
 *   • cleans up the pending invite row on decline,
 *   • returns a `{ success, error }` envelope the client can surface.
 *
 * On accept we also invalidate `circles-schedule` etc. so the new
 * colleague's schedule appears immediately on the calendar.
 */
export function useCircleInviteActions() {
  const queryClient = useQueryClient();

  const resolveBuddyInvite = useMutation({
    mutationFn: async ({
      notif,
      accept,
    }: {
      notif:  DBNotification;
      accept: boolean;
    }) => {
      if (!notif.sender_id) throw new Error("Could not identify the sender.");

      const { data, error } = await supabase.rpc("respond_circle_invite", {
        p_sender_id:    notif.sender_id,
        accept_boolean: accept,
      });

      if (error) throw error;
      const result = data as { success?: boolean; error?: string } | null;
      if (result?.error) throw new Error(result.error);

      return { id: notif.id, accept };
    },
    onMutate: async ({ notif }) => {
      await queryClient.cancelQueries({ queryKey: INBOX_KEY });
      const prev = queryClient.getQueryData<DBNotification[]>(INBOX_KEY);
      // Remove optimistically
      queryClient.setQueryData<DBNotification[]>(INBOX_KEY, (old = []) =>
        old.filter((n) => n.id !== notif.id),
      );
      return { prev };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: HISTORY_KEY });
      // Reload queries to show the colleague immediately
      queryClient.invalidateQueries({ queryKey: circleMembersPrefix });
      queryClient.invalidateQueries({ queryKey: circlesSchedulePrefix });
      queryClient.invalidateQueries({ queryKey: ["buddy-ids"] });
      // Success is silent: row animates out; calendar refreshes. No toast.
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(INBOX_KEY, ctx.prev);
      toast({
        title:       "Could not process invitation",
        description: "Please try again.",
        variant:     "destructive",
      });
    },
  });

  return { resolveBuddyInvite };
}
